import { useEffect } from 'react';
import { StandaloneAuth } from '@leagueplatform/auth-standalone';
import { LoadingIndicator } from '@leagueplatform/web-common-components';
import { terminateLiveChatSession } from 'utils/terminate-live-chat-session';

const DEEP_LINK_TIMEOUT_MS = 1500;

const isMobileDevice = () =>
  /iPad|iPhone|iPod|Android/.test(navigator.userAgent) &&
  !window.matchMedia('(hover: hover)').matches;

const isEmbeddedView = () => window.self !== window.top;

const attemptNativeAppSignOut = (): Promise<boolean> =>
  new Promise((resolve) => {
    let timer: ReturnType<typeof setTimeout>;

    const onVisibilityChange = () => {
      if (document.hidden) {
        clearTimeout(timer);
        document.removeEventListener('visibilitychange', onVisibilityChange);
        resolve(true);
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    timer = setTimeout(() => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      resolve(false);
    }, DEEP_LINK_TIMEOUT_MS);

    // Use a hidden iframe to attempt the deep link so the current page's
    // JS context is preserved if the scheme is unrecognized by the browser.
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = 'league://sign-out';
    document.body.appendChild(iframe);
  });

export const AuthViewLogout = () => {
  useEffect(() => {
    (async () => {
      if (isMobileDevice()) {
        // Attempt deep link, but do not return early.
        // We must still log out of the web session because the native app
        // cannot clear the mobile browser's local cookies or tokens.
        await attemptNativeAppSignOut();
      }

      try {
        terminateLiveChatSession();
      } catch {
        // no-op: logout should still proceed
      }
      const params = new URLSearchParams(window.location.search);
      const redirectUrl = params.get('redirect');
      let safeRedirectUrl = window.location.origin;

      if (redirectUrl) {
        try {
          const candidateUrl = new URL(
            decodeURIComponent(redirectUrl),
            window.location.origin,
          );
          const isSameOrigin = candidateUrl.origin === window.location.origin;
          const isTrustedTelusShell = candidateUrl.hostname.endsWith(
            '.telushealthspace.com',
          );
          const isTrustedAlbertaSignOut =
            candidateUrl.hostname === 'console.myhealthrecords.alberta.ca';
          if (isSameOrigin || isTrustedTelusShell || isTrustedAlbertaSignOut) {
            safeRedirectUrl = candidateUrl.toString();
          }
        } catch {
          // no-op: fallback to origin if redirect is malformed/unsafe
        }
      }

      if (isEmbeddedView()) {
        await StandaloneAuth.client.logout({ localOnly: true });
        window.location.replace(safeRedirectUrl);
        return;
      }

      await StandaloneAuth.client.logout({
        returnTo: safeRedirectUrl,
        federated: true,
      });
    })();
  }, []);

  return <LoadingIndicator />;
};

export const AuthViewLogin = () => {
  useEffect(() => {
    StandaloneAuth.client.loginWithRedirect();
  }, []);

  return <LoadingIndicator />;
};
